"use client";

import { useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { XIcon } from "lucide-react";

import { Button } from "@tripcart/traveler-ui/components/button";

import {
  DURATION_BANDS,
  MONTH_LABELS,
  PRICE_DISPLAY_MAX,
  parseState,
  splitCsv,
} from "./_filters-shared";
import type { FilterOption } from "./_filter-panel.client";
import { scrollToResultsTopIfScrolledPast } from "./_scroll";
import { useFormatMajorMoney } from "@/lib/currency-format-context";
import { useTranslator } from "@/lib/translation-context";

// Not `styles`: that name is the component's style-facet prop.
import css from "./_active-chips.client.module.css";

interface Chip {
  key: string;
  label: string;
  remove: (p: URLSearchParams) => void;
}

// Every param "Clear all" must drop. `facet` carries the operator's custom
// filters: leaving it here cleared the destination chip while a custom filter
// stayed silently applied, so the results never matched the empty chip row.
const FILTER_KEYS = [
  "q",
  "dur",
  "priceMin",
  "priceMax",
  "months",
  "dest",
  "style",
  "facet",
];

export function ActiveChips({
  destinations,
  styles,
  catalogMaxPrice,
}: {
  destinations: FilterOption[];
  styles: FilterOption[];
  catalogMaxPrice?: number;
}): React.ReactElement | null {
  const router = useRouter();
  const sp = useSearchParams();
  const state = parseState((k) => sp.get(k) ?? undefined);
  const effectiveMax = catalogMaxPrice ?? PRICE_DISPLAY_MAX;
  const formatMajorMoney = useFormatMajorMoney();

  const nameOf = (opts: FilterOption[], slug: string) =>
    opts.find((o) => o.slug === slug)?.name ?? slug;

  const t = useTranslator();
  const [, startTransition] = useTransition();

  function apply(mutate: (p: URLSearchParams) => void): void {
    const p = new URLSearchParams(sp.toString());
    mutate(p);
    p.delete("page");
    startTransition(() => {
      router.replace(`?${p.toString()}`, { scroll: false });
    });
    scrollToResultsTopIfScrolledPast();
  }
  const removeFromCsv =
    (key: string, value: string) => (p: URLSearchParams) => {
      const next = splitCsv(p.get(key)).filter((v) => v !== value);
      if (next.length) p.set(key, next.join(","));
      else p.delete(key);
    };

  const chips: Chip[] = [];
  if (state.q) {
    chips.push({
      key: "q",
      label: `“${state.q}”`,
      remove: (p) => p.delete("q"),
    });
  }
  for (const slug of state.dest) {
    chips.push({
      key: `dest-${slug}`,
      label: nameOf(destinations, slug),
      remove: removeFromCsv("dest", slug),
    });
  }
  for (const slug of state.style) {
    chips.push({
      key: `style-${slug}`,
      label: nameOf(styles, slug),
      remove: removeFromCsv("style", slug),
    });
  }
  if (state.dur) {
    const band = DURATION_BANDS.find((b) => b.id === state.dur);
    chips.push({
      key: "dur",
      label: band?.label ?? state.dur,
      remove: (p) => p.delete("dur"),
    });
  }
  if (sp.get("priceMin") || sp.get("priceMax")) {
    const hi =
      state.priceMax >= effectiveMax
        ? `${formatMajorMoney(effectiveMax)}+`
        : formatMajorMoney(state.priceMax);
    chips.push({
      key: "price",
      label: `${formatMajorMoney(state.priceMin)} – ${hi}`,
      remove: (p) => {
        p.delete("priceMin");
        p.delete("priceMax");
      },
    });
  }
  for (const m of state.months) {
    const [y, mo] = m.split("-");
    const label = `${MONTH_LABELS[Number(mo)] ?? mo} ${y}`;
    chips.push({
      key: `month-${m}`,
      label,
      remove: removeFromCsv("months", m),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className={css.row}>
      {chips.map((chip) => (
        <Button
          key={chip.key}
          variant="outline"
          size="sm"
          className={css.chip}
          /* The visible text is the filter's VALUE ("Nepal", "“petra”"), so
             on its own the control announces as "Nepal, button" and never
             says what pressing it does. Two parts, not one interpolated
             `t()` call: `pnpm i18n` scrapes string literals, and a template
             argument scrapes to nothing. */
          aria-label={`${t("Remove filter")}: ${chip.label}`}
          onClick={() => apply(chip.remove)}
        >
          {chip.label}
          <XIcon strokeWidth={2.5} aria-hidden />
        </Button>
      ))}
      <Button
        variant="ghost"
        size="sm"
        tone="danger"
        onClick={() =>
          apply((p) => {
            for (const k of FILTER_KEYS) p.delete(k);
          })
        }
        className={css.clearAll}
      >
        {t("Clear All")}
      </Button>
    </div>
  );
}
